/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, { Component } from 'react';

import {Link, Redirect, withRouter} from "react-router-dom";

import AppNavbar from "./../components/AppNavbar";
import AppFooter from "../components/AppFooter";
import WaitingComponent from "../components/WaitingComponent";
import {isUserSignedIn, UserContext} from "../UserContext";

import {
    intervalIdFromParams,
    FING_INVALIDINPUT_REDEEM_CODE, enrichOfferOptions, matchesCaseInsensitive, productIdFromParams
} from "../helpers/BillingHelper";
import PlanSummary from "./PlanSummary";
import AccountSignedOutEmptyState from "./AccountSignedOutEmptyState";
import PurchaseSetup from "../assets/svg/stories/premium-purchase-setup.svg";


class SubscribeRedeem extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);
        this.state = {
            offers: [],
            subscription: {},
            codeError: null,
            serverError: null,
            submitting: false
        };
        this.codeInput = React.createRef();
    }

    componentDidMount() {
        fetch('/rest/billing/offers?mode=redeem')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(result => {
                if (result && result.options) {
                    enrichOfferOptions(result.options);
                }
                this.setState({ offers: result });
            })
            .catch(e => this.catchRestError(e));
        window.scrollTo(0, 0)
        document.title = "Fing - Redeem code";
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
        console.log("Failed to fetch plans: " + response);
    }

    // --------------------------------------------------------------------------------

    render() {
        return (
            <div>
                <AppNavbar mode='account' fillBackground="true" />
                <main id="content" role="main">
                    {this.renderMainContent()}
                </main>
                <AppFooter />
            </div>
        );
    }

    renderMainContent() {
        return (
            <UserContext.Consumer>
                {userContext => this.renderMainContentInContext(userContext)}
            </UserContext.Consumer>
        )
    }

    renderMainContentInContext(userContext) {
        const { subscription } = this.state;
        if (subscription && subscription.created) {
            return (<Redirect to='/account/subscribe/complete' />);
        }

        if (isUserSignedIn(userContext)) {
            return this.renderPurchasePlan(userContext.userProfile);
        } else {
            return <AccountSignedOutEmptyState />;
        }
    }

    renderPurchasePlan(userProfile) {
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;
        const selectedPlan = this.getSelectedPlan();
        return (
            <div className="container space-2">
                <div className="row justify-content-md-between align-items-md-start mb-10">
                    <div className="col-md-7 col-xl-6">
                        <div className="mb-6">
                            <h1 className="display-5 mb-3">Redeem your code.</h1>
                            <p className="lead">{name}, if you have a personal redeem code, the full potential of the Fing Premium is just a click away.</p>
                        </div>
                        <div>
                            {userProfile && this.renderPlanSummary(selectedPlan)}
                        </div>
                        <div>
                            {userProfile && this.renderEnterCode(userProfile)}
                        </div>
                    </div>
                    <div className="col-md-5 col-xl-6">
                        <img className="img-fluid" src={PurchaseSetup}
                             alt="Image Description" />
                    </div>
                </div>
            </div>
        );
    }

    // --------------------------------------------------------------------------------

    groupCls = (hasError) => "input-group mb-3 js-form-message js-focus-state" +
        (hasError ? " has-error" : "");

    handleSubmitNoOp = async (event) => {
        // Block native form submission.
        event.preventDefault();
    };

    handleSubmitRedeemCode = async (event) => {
        event.preventDefault();

        const selectedPlan = this.getSelectedPlan();
        let hasErrors = false;

        const codeValue = this.codeInput.current.value;
        const codeError = !codeValue || codeValue.length === 0;
        hasErrors |= codeError;

        if (hasErrors) {
            this.setState({
                codeError: codeError,
                submitting: false
            });
            return;
        } else {
            this.setState({
                codeError: false,
                submitting: true
            });
        }

        fetch('/rest/billing/subscription/redeem', {
            method: 'post',
            mode: 'cors',
            credentials: 'same-origin', // send cookies
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                product: selectedPlan.productId,
                interval: selectedPlan.billingCycle.toUpperCase(),
                redeemCode: codeValue
            }),
        }).then(function (response) {
            if (response.status === 409) {  // Conflict
                throw Error("A subscription is already running. Go to your Account&Settings dashboard to check it");
            } else if (response.status !== 200) {
                throw Error(response.statusText);
            }
            return response;
        }).then(function (result) {
            return result.json();
        }).then((response) => {
            if (response.errorState) {
                if (response.errorState.invalidInput === FING_INVALIDINPUT_REDEEM_CODE) {
                    this.setState({codeError: true});
                } else if (response.errorState.message) {
                    this.setState({serverError: response.errorState.message});
                }
            } else if (response.subscription) {
                this.setState({ subscription: response.subscription });
            }
        }).catch(error => {
            this.setState({
                serverError: error.message || error
            });
        }).finally(() => {
            this.setState({
                submitting: false
            });
        });
    };

    getSelectedPlan() {
        const query = new URLSearchParams(this.props.location.search);
        const productId = productIdFromParams(query);
        const intervalId = intervalIdFromParams(query);

        // From state
        const {offers} = this.state;
        const hasOffers = offers && offers.options && offers.options.length > 0;
        const selectedOffer = hasOffers ? offers.options.find(offer =>
            matchesCaseInsensitive(offer.billingCycle, intervalId) &&
            matchesCaseInsensitive(offer.productId, productId)) : null;
        return selectedOffer;
    }

    renderEnterCode(userProfile) {
        const { submitting } = this.state;

        return (
            <div>
                <h3>Enter your redeem code</h3>
                <form key="redeem_form" onSubmit={submitting === true ?
                    this.handleSubmitNoOp : this.handleSubmitRedeemCode}>
                    {this.renderRedeemField()}
                    {this.renderStartPaymentButton(userProfile)}
                </form>
            </div>
        );
    }

    renderRedeemField() {
        const { codeError, submitting } = this.state;

        const query = new URLSearchParams(this.props.location.search);
        const redeemCode = query.get("redeem_code") || '';
        return (
            <fieldset id="redeem-code-fields" className="mb-2">
                <div className={this.groupCls(codeError)}>
                    {/*<div className="input-group-prepend">*/}
                    {/*    <label className="input-group-text min-w-15rem" id="name-addon">Name</label>*/}
                    {/*</div>*/}
                    <input ref={this.codeInput}
                           type="text"
                           name="code"
                           className="form-control shadow-none"
                           placeholder="Your code, e.g. XYZ"
                           defaultValue={redeemCode}
                           aria-label="Redeem Code"
                           aria-describedby="name-addon"
                           readOnly={submitting === true ? "readonly" : ""}
                    />
                    {
                        codeError === true &&
                        <div id="code-error"
                             className="invalid-feedback d-block">
                            Redeem code not valid. Check that you are signed in with the account the code is assigned to.
                        </div>
                    }
                </div>
            </fieldset>
        );
    }

    renderStartPaymentButton() {
        const { submitting, serverError } = this.state;
        const selectedPlan = this.getSelectedPlan();

        const query = new URLSearchParams(this.props.location.search);
        if (this.codeInput.current && this.codeInput.current.value)
            query.set("redeem_code", this.codeInput.current.value);

        return (
            <div>
                <button className="btn btn-primary w-100"
                        type="submit" disabled={submitting === true || selectedPlan === null}>
                    <i className="fa fa-fw fa-gift mr-2"/>Activate subscription
                </button>
                {/*<Link className="btn btn-link btn-wide"*/}
                {/*      to={`/account/subscribe/setup?${query.toString()}`}>Change billing cycle</Link>*/}

                {
                    submitting === true &&
                    <div className="mt-4">
                        <WaitingComponent/>
                    </div>
                }
                {
                    serverError &&
                    <div id="server-error"
                         className="invalid-feedback d-block">
                        {serverError}.
                    </div>
                }

                <p className="pt-3">
                    No commitments, cancel online any time.
                </p>
                <p className="small">
                    By clicking on "Redeem code", you agree to
                    our <a href="https://www.fing.com/fing-terms-of-service">Terms of Service</a> and <a
                    href="https://www.fing.com/fing-privacy-policy">Privacy Policy</a>.
                    Fing will automatically continue your subscription at the end of your free trial and
                    charge the subscription fee to your payment method on a regular basis until you cancel.
                    To setup your credit card or to cancel, go to the "Account & Settings" page and follow
                    the instructions for cancellation.
                </p>
            </div>
        );
    }

    renderPlanSummary(plan) {
        return (
            <div className="mb-6">
                <h3>Your Plan</h3>
                <div className="card card-bordered shadow-none">
                    <div className="card-body py-2">
                        <PlanSummary plan={plan} />
                    </div>
                </div>
            </div>
        );
    }

}

export default withRouter(SubscribeRedeem)